'use client';

import { FC, useEffect } from 'react';

import { UnitSystem } from '@ravago/shared/page-data/models/config';
import {
   BasicFacetWithTermsV2,
   CollectionFacetsV2,
   collectionGroups,
   FacetFilterTracking,
   facetLabelTranslationMappingV2
} from '@ravago/shared/page-data/models/elastic';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Expand } from '@ravago/shared/radiance/components/expand/components/Expand/Expand';
import { FlyoutActions, FlyoutContent, FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import isNil from 'lodash/isNil';

import { Link } from '../../../../../shared/link/link';
import { FacetGroupStructureV2 } from '../../../models/facet-group.model';
import { getActiveFacetLabelsV2 } from '../../../utils/filter-label-utils';
import { getFacetTranslationPlaceholders } from '../../../utils/v2-mappers-utils';
import { FacetGroupV2 } from '../filters/facet-group-v2';

const groupKeyOrderMap = new Map([
   ['group', 0],
   ['subgroup', 1],
   ['technical-properties', 2],
   ['filler', 3],
   ['feature', 4],
   ['quality', 5],
   ['brand', 6],
   ['producer', 7],
   ['processing-method', 8],
   ['visibility', 9],
   ['industry', 10],
   ['sustainability-group', 11],
   ['country', 12]
]);

interface Props {
   activeFacets: Array<BasicFacetWithTermsV2>;
   combinedFacetGroupStructure: Array<FacetGroupStructureV2>;
   canResetFacets: boolean;
   filterFlyoutOpen?: boolean;
   language: string;
   locked?: boolean;
   unitSystem: UnitSystem;
   expandedGroups: string[];
   onFacetsAdjust: (value: Array<BasicFacetWithTermsV2>, switchToList?: boolean) => void;
   onFacetClicked: (facetFilter: FacetFilterTracking) => void;
   onFacetReset: () => void;
   onExpandChange: (key: string, expanded: boolean) => void;
   onCloseAll: () => void;
   onFilterFlyoutClose: () => void;
}

export const ProductPortfolioFiltersV2: FC<Props> = ({
   activeFacets,
   combinedFacetGroupStructure,
   canResetFacets,
   filterFlyoutOpen = false,
   language,
   locked = false,
   unitSystem,
   expandedGroups,
   onFacetsAdjust,
   onFacetReset,
   onFacetClicked,
   onExpandChange,
   onCloseAll,
   onFilterFlyoutClose
}) => {
   const translate = useTranslations();
   const removeFacetGroup = (key: string) => {
      const newFacets = activeFacets.filter((facet) => facet.facetName !== key);
      onFacetsAdjust(newFacets);
   };

   const onFacetsChange = (changedFacets: BasicFacetWithTermsV2[], switchToList?: boolean) => {
      const newBasicFacetWithTerms = [...activeFacets];

      changedFacets.forEach((changedFacet) => {
         if (changedFacet.terms.length === 0) {
            const index = newBasicFacetWithTerms.findIndex((facetFilter) => facetFilter.facetName === changedFacet.facetName);
            if (index !== -1) {
               newBasicFacetWithTerms.splice(index, 1);
            }
            return;
         }
         // Remove range key when no min/max values are set
         changedFacet.terms.forEach((term) => {
            if (term.range && (isNil(term.range.min) || isNil(term.range.max))) {
               delete term.range;
            }
         });

         const index = newBasicFacetWithTerms.findIndex((facetFilter) => facetFilter.facetName === changedFacet.facetName);

         if (index === -1) {
            newBasicFacetWithTerms.push(changedFacet);
         } else {
            newBasicFacetWithTerms[index] = changedFacet;
         }
      });
      onFacetsAdjust(newBasicFacetWithTerms, switchToList);
   };

   const handleReset = () => {
      onCloseAll();
      onFacetReset();
   };

   useEffect(() => {
      if (filterFlyoutOpen) onCloseAll();
   }, [filterFlyoutOpen]);

   const getActiveNestedFacet = (facetGroup: FacetGroupStructureV2) =>
      // currently only supports nesting of one facet within another
      activeFacets.find(
         (filter) => filter.facetName === facetGroup.facets?.find((f) => f.nestedFacetGroups)?.nestedFacetGroups?.[0]?.identifier?.key
      );

   // Sort facetGroups according to groupKeyOrder
   const sortedFacetGroups = [...combinedFacetGroupStructure].sort((a, b) => {
      const indexA = groupKeyOrderMap.get(a.identifier.key) ?? -1;
      const indexB = groupKeyOrderMap.get(b.identifier.key) ?? -1;

      // If both are in the order map, sort by their position
      if (indexA !== -1 && indexB !== -1) {
         return indexA - indexB;
      }
      // If only a is in the order map, it comes first
      if (indexA !== -1) {
         return -1;
      }
      // If only b is in the order map, it comes first
      if (indexB !== -1) {
         return 1;
      }
      // If neither is in the order map, maintain original order
      return 0;
   });

   const allActiveFacets = (facetGroup: FacetGroupStructureV2): BasicFacetWithTermsV2 | undefined => {
      if (facetGroup.identifier.collectionGroup) {
         return {
            facetName: facetGroup.identifier.key,
            terms: activeFacets
               .filter((facet) => collectionGroups[facetGroup.identifier.key as keyof CollectionFacetsV2]?.includes(facet.facetName as any))
               .flatMap((facet) => facet.terms)
         };
      }
      return activeFacets.find((filter) => filter.facetName === facetGroup.identifier.key);
   };

   const renderedFacetGroups = sortedFacetGroups.map((facetGroup) => (
      <FacetGroupV2
         key={facetGroup.identifier.key}
         language={language}
         activeFacet={allActiveFacets(facetGroup)}
         activeNestedFacet={getActiveNestedFacet(facetGroup)}
         facetGroupStructure={facetGroup}
         onFacetsChange={onFacetsChange}
         onFacetClicked={onFacetClicked}
         expanded={expandedGroups.includes(facetGroup.identifier.key)}
         onExpandToggle={(expanded) => onExpandChange(facetGroup.identifier.key, expanded)}
      />
   ));

   return (
      <div style={{ pointerEvents: locked ? 'none' : 'unset', display: 'contents' }}>
         <BreakpointSwitch includeViews={['desktop']}>
            <Box direction="column" gap="lg">
               <Box justify="between" items="baseline">
                  <Typography color="strong" variant="headline">
                     {translate('products-overview-filters-header')}
                  </Typography>
                  {canResetFacets && <Link onClick={handleReset}>{translate('products-overview-filters-reset')}</Link>}
               </Box>
               <Expand expanded={Object.keys(activeFacets).length > 0}>
                  <Box direction="column" gap="sm" spacing={{ bottom: 'md' }}>
                     <Typography variant="body-highlight" color="strong">
                        {translate('products-overview-filters-applied')}
                     </Typography>

                     {activeFacets.map((facet) => {
                        const facetGroup = combinedFacetGroupStructure.find(
                           (f) =>
                              f.identifier.key === facet.facetName ||
                              f.facets.some((facetStructure) =>
                                 facetStructure.nestedFacetGroups?.some((nestedFacet) => nestedFacet.identifier.key === facet.facetName)
                              ) ||
                              f.facets.some((facetStructure) => facetStructure.key === facet.facetName)
                        );
                        if (!facetGroup || facetGroup.facets.length === 0) return null;

                        return (
                           <Box key={facet.facetName} justify="between" items="baseline">
                              <Typography variant="body" color="strong" maxLines="2">
                                 {`${translate(
                                    facetLabelTranslationMappingV2.get(facet.facetName) ?? facet.facetName,
                                    getFacetTranslationPlaceholders(facet.facetName, unitSystem)
                                 )}: `}
                                 <b>{getActiveFacetLabelsV2(facet, facetGroup)}</b>
                              </Typography>
                              <Link onClick={() => removeFacetGroup(facet.facetName)}>
                                 <Icon icon="times" />
                              </Link>
                           </Box>
                        );
                     })}
                  </Box>
               </Expand>
            </Box>
            <Box direction="column" gap="md">
               {renderedFacetGroups}
            </Box>
         </BreakpointSwitch>
         <BreakpointSwitch excludeViews={['desktop']}>
            <FlyoutHeader>{translate('products-overview-filters-header')}</FlyoutHeader>
            <FlyoutContent>{renderedFacetGroups}</FlyoutContent>
            <FlyoutActions>
               <Button onClick={onFilterFlyoutClose}>{translate('filter.show-results')}</Button>

               {canResetFacets && (
                  <Button variant="secondary" onClick={handleReset}>
                     {translate('products-overview-filters-reset')}
                  </Button>
               )}
            </FlyoutActions>
         </BreakpointSwitch>
      </div>
   );
};
